import { useCallback, useMemo, useRef } from 'react'
import { useQuery } from '@apollo/client'
import { GET_SPACES } from 'modulesCommon/graphql/queries'
import compact from 'lodash/fp/compact'
import uniq from 'lodash/fp/uniq'
import { Space } from 'shared/__gql-types'

export interface ISpaceOption {
  id: string
  name: string
  subtitle: ReadonlyArray<string>
}

export function spaceOptionAdapter(data: Space): ISpaceOption {
  return {
    id: data?.id ?? '',
    name: data?.name ?? '',
    subtitle: (data?.labels ?? []).map((label) => label?.alias ?? '')
  }
}

/**
 * 
 * @returns @deprecated
 * Use src/modulesCommon/serviceHooks/useSpaces.ts instead
 */
export const useSpaces = () => {

  const regionNames = useRef(new Intl.DisplayNames(['en'], { type: 'region' }))

  const { data } = useQuery<{ getSpaces: ReadonlyArray<Space> }>(GET_SPACES)

  const byId = useMemo(
    () => (data?.getSpaces || []).reduce<Record<string, Space>>((a, v) => ({
      ...a,
      [v?.id ?? '']: v,
    }), {}),
    [data?.getSpaces],
  )

  const spaceOptions = useMemo<ReadonlyArray<ISpaceOption>>(
    () => (data?.getSpaces || []).map(spaceOptionAdapter),
    [data?.getSpaces],
  )

  const getCountryOptions = useCallback(
    (id?: string): { label: string; value: string }[] => {
      if (typeof id !== 'string') return []
      return uniq(
        (byId[id]?.labels ?? []).reduce<string[]>(
          (a, v) => a.concat(compact(v?.country_codes ?? [])),
          []
        )
      )
        .map(value => ({ label: regionNames.current.of(value) || '', value }))
    },
    [byId],
  )

  return {
    getRegionName: (value: string) => regionNames.current.of(value),
    byId,
    spaceOptions,
    getCountryOptions,
  }
}
